import React from 'react';
import { Link } from 'react-router-dom';
import { ClosingCta } from '../common/B2B';
import { RelatedSection } from '../common/B2BSections';
import { ARTICLES, categoryBySlug, relatedTo, formatDate } from './articles';

// Plantilla común de artículos y guías (nov-2026, diseño B2B):
//  - franja azul marino con miga de pan (Artículos / categoría), H1, entradilla
//    y fecha de revisión;
//  - columna de lectura (~720 px) con los estilos de .article-prose (index.css)
//    y, en escritorio, índice lateral fijo con los H2 que se le pasen en `toc`;
//  - artículos relacionados (registro de articles.js) y cierre en azul marino.
// El contenido va como hijos DIRECTOS (h2 con id, h3, p, ul, ol, blockquote y
// los bloques de ./blocks): los estilos de lectura solo se aplican a los hijos
// directos, para no pisar los de los bloques. La portada sale de `image` en
// articles.js.

const NAVY = '#0b1d39';

const ArticleLayout = ({
  url,
  title,
  deck,
  updated,
  readingMinutes,
  toc = [],
  closing = { title: '¿Quiere verlo con sus propios documentos?', text: 'Le enseñamos cómo lo resuelve BlueCMR en su operativa, sin compromiso.' },
  children,
}) => {
  const meta = ARTICLES.find((a) => a.url === url);
  const category = meta ? categoryBySlug(meta.category) : null;
  const date = updated || meta?.updated;
  const related = relatedTo(url, 3).map((a) => ({ title: a.title, text: a.text, to: a.url }));

  return (
    <div className="bg-white">
      <section style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <nav className="text-sm font-semibold text-blue-300 mb-6" aria-label="Miga de pan">
            <Link to="/articulos" className="hover:text-white">Artículos</Link>
            {category && (
              <>
                <span className="mx-2 text-blue-300/60">/</span>
                <Link to={`/articulos#${category.slug}`} className="hover:text-white">{category.title}</Link>
              </>
            )}
          </nav>
          <h1 className="max-w-4xl text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold tracking-tight text-white leading-[1.12]">{title}</h1>
          {deck && <p className="mt-6 max-w-3xl text-lg sm:text-xl text-slate-300 leading-relaxed">{deck}</p>}
          {(date || readingMinutes) && (
            <p className="mt-8 text-sm text-slate-400">
              {meta?.kind || 'Artículo'}
              {date && <> · Revisado el {formatDate(date)}</>}
              {readingMinutes && <> · {readingMinutes} min de lectura</>}
            </p>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          {toc.length > 0 && (
            <aside className="hidden lg:block lg:col-span-3">
              <nav className="sticky top-24" aria-label="En este artículo">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-4">En este artículo</p>
                <ol className="border-l border-slate-200">
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className="block -ml-px border-l border-transparent pl-4 py-1.5 text-sm leading-snug text-slate-600 hover:text-primary-700 hover:border-primary-700">
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </aside>
          )}
          <article className={`article-prose min-w-0 ${toc.length > 0 ? 'lg:col-span-8 lg:col-start-5' : 'lg:col-span-8 lg:col-start-3'} max-w-[720px]`}>
            {meta?.image && (
              <img src={meta.image} alt={meta.imageAlt || ''} width="1536" height="864" className="block w-full h-auto mb-10" fetchpriority="high" />
            )}
            {children}
          </article>
        </div>
      </div>

      {related.length > 0 && <RelatedSection title="Siga leyendo" items={related} />}
      <ClosingCta title={closing.title} text={closing.text} lang="es" />
    </div>
  );
};

export default ArticleLayout;
